import React, { useEffect, useState } from 'react';
import { call, getToken } from './api.js';
import { withReturn, hereNow } from './gate.jsx';
import { countLabel } from './rails.js';
import { VenueCard, TabBar } from './ui.jsx';
import { shareSaved } from './share.js';

/* ══════════════════════════════════════════════════════════════════════════
 * SAVED — the places an Eleater kept
 *
 * From `08a_Saved.html`: the heading "Saved" and the empty line "No saved
 * places yet", quoted rather than reworded.
 *
 * ── IT NEEDS A SIGN-IN, AND SAYS SO ─────────────────────────────────────────
 *
 * ⚠️ GET /restaurants/saved/me is requireAuth + requireType('eleater'). A
 * signed-out Eleater opening this gets 401, and a screen that renders the 401
 * as an empty list would be telling them they have saved nothing — which is
 * a different and much worse sentence than "sign in to see these". So the
 * token is checked BEFORE the call, and the call is not made at all.
 *
 * ⚠️ AND SAVED RETURNED A DIFFERENT VENUE SHAPE UNTIL TODAY. listSaved sent
 * logo_url and gallery_urls with no rating, price band or area, so the one
 * card the app draws everywhere would have rendered a saved venue blank while
 * the same venue on Home looked complete. Fixed in the service, not worked
 * around here — a card that copes with two shapes is two cards.
 * ══════════════════════════════════════════════════════════════════════════ */

export default function Saved() {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);
  const signedIn = !!getToken();

  useEffect(() => {
    if (!signedIn) return;
    let live = true;
    call('/restaurants/saved/me', { auth: true })
      .then((d) => live && setRows(Array.isArray(d) ? d : (d.results || [])))
      .catch((e) => live && setErr(e.message));
    return () => { live = false; };
  }, [signedIn]);

  /* ⚠️ THE CARD DOES THE UNSAVING — this only drops the row from a list of
     SAVED places once it has stopped being one. `VenueCard` owns the tap, the
     write and the failure, on every screen, so there is no second copy of
     "how to unsave" living here to drift from it. */
  function dropRow(v) {
    setRows((prev) => (prev || []).filter((r) => r.id !== v.id));
  }

  return (
    <div className="home">
      <div className="top">
        <a className="back" href="?profile=1" aria-label="Profile">‹</a>
        <h1 className="heroline">Saved</h1>
      </div>
      {/* ⚠️ `08a_Saved.html .subh` — the line that says what this screen is
          and how to use it. It was missing entirely. */}
      <p className="subh">Restaurants you’ve hearted · tap to open</p>

      <main>
        {!signedIn && (
          <>
          <p className="mut">
            Sign in to see the places you’ve saved.
          </p>
          <div className="guest">
            <a className="gcta" href={withReturn('?welcome=1', hereNow())}>Sign in or join Eleat</a>
          </div>
          </>
        )}

        {signedIn && err && <p className="err">{err}</p>}
        {signedIn && !rows && !err && <p className="mut">Loading…</p>}

        {signedIn && rows && (
          <>
            {/* ⚠️ NO COUNT LINE WHEN THERE IS NOTHING. "0 places" above "No
                saved places yet" says the same thing twice, the second time
                in a number. */}
            {rows.length > 0 && (
              <div className="cntline">
                <b>{countLabel(rows.length)}</b>
                {` place${rows.length === 1 ? '' : 's'}`}
              </div>
            )}

            {/* the locked screen's own line */}
            {/* ⚠️ THE SECOND LINE IS THE WHOLE POINT — `08a_Saved.html`.
                "No saved places yet" alone tells somebody they have nothing;
                it does not tell them how to get something. The locked screen
                says where the heart is. Al: *"where are the examples in
                saved"* — there is nothing to show until he hearts a place, so
                the screen has to say how. */}
            {rows.length === 0 && (
              <>
                <p className="mut">No saved places yet</p>
                <p className="mut">
                  Tap the heart on any restaurant to keep it here for later.
                </p>
              </>
            )}

            {/* ⚠️ `onRemove` IS NOT A DIFFERENT HEART — the heart behaves the
                same here as on Home. It is Saved asking to be told, so the row
                can leave the list. `.sharebtn` is the locked Saved card's own
                second control and stays this screen's. */}
            <div className="grid">
              {rows.map((v) => (
                <VenueCard key={v.id} v={v}
                  onRemove={dropRow}
                  share={{ ...shareSaved(v), venueId: v.id, subjectType: 'restaurant' }} />
              ))}
            </div>
          </>
        )}
      </main>

      <TabBar active="saved" />
    </div>
  );
}
